import Loader from 'components/_UI/Loader'
import Text from 'components/_UI/Text'
import React, { useState, useEffect } from 'react'
import { Tag, Check, MapPin } from 'lucide-react'
import { Column } from 'styled/alignment/Column'
import colors from 'theme/colors'
import { useTags } from 'context/events/Tags'
import styled from 'styled-components'
import breakpoints from 'theme/breakpoints'
import { getCategoryIcon } from 'modules/events/categories/categoryIconMap'

export default function CategoriesInfo({ tagsLoading, error, edit, setSelecteds, onIconChange }) {
    const { tags } = useTags();
    const MAX_TAGS = 3;

    const [selectedItems, setSelectedItems] = useState(edit?.map(item => item.id) || []);
    const [shakeId, setShakeId] = useState(null);
    const [selectedIcon, setSelectedIcon] = useState(null);

    // Quando as tags editadas carregam, pre-seleciona o ícone do thumb salvo
    useEffect(() => {
        if (edit) {
            const items = edit?.map(item => item?.id)
            setSelectedItems(items);
            setSelecteds(items);

            // Auto-seleciona o ícone da primeira tag se houver apenas uma
            if (edit.length === 1 && edit[0]?.icon) {
                setSelectedIcon(edit[0].icon);
                onIconChange?.(edit[0].icon);
            }
        }
    }, [error, tagsLoading])

    // Sempre que a seleção muda, atualiza o ícone automaticamente
    useEffect(() => {
        if (!tags) return;

        const selectedTagObjects = tags.filter(t => selectedItems.includes(t.id));

        if (selectedTagObjects.length === 0) {
            setSelectedIcon(null);
            onIconChange?.(null);
            return;
        }

        if (selectedTagObjects.length === 1) {
            // Auto-seleciona quando só tem 1
            const icon = selectedTagObjects[0].icon;
            setSelectedIcon(icon);
            onIconChange?.(icon);
            return;
        }

        // Com 2+ tags, mantém o ícone atual se ele ainda é válido, senão usa o primeiro
        const validIcons = selectedTagObjects.map(t => t.icon);
        if (!validIcons.includes(selectedIcon)) {
            const firstIcon = validIcons[0];
            setSelectedIcon(firstIcon);
            onIconChange?.(firstIcon);
        }
    }, [selectedItems, tags]);

    const handleSelectItem = (id) => {
        setSelectedItems(prevSelectedItems => {
            let newSelectedItems;

            if (prevSelectedItems.includes(id)) {
                newSelectedItems = prevSelectedItems.filter(item => item !== id);
            } else {
                if (prevSelectedItems.length >= MAX_TAGS) {
                    setShakeId(id);
                    setTimeout(() => setShakeId(null), 500);
                    return prevSelectedItems;
                }
                newSelectedItems = [...prevSelectedItems, id];
            }

            setSelecteds(newSelectedItems);
            return newSelectedItems;
        });
    };

    const handleIconSelect = (iconKey) => {
        setSelectedIcon(iconKey);
        onIconChange?.(iconKey);
    };

    const isMaxReached = selectedItems.length >= MAX_TAGS;

    // Tags selecionadas com objeto completo (para mostrar ícones)
    const selectedTagObjects = tags?.filter(t => selectedItems.includes(t.id)) || [];
    const showIconPicker = selectedTagObjects.length >= 2;

    return (
        <Column>
            <SectionHeader>
                <SectionIcon><Tag size={18} color={colors.occasionalPurple} /></SectionIcon>
                <HeaderContent>
                    <Text type='bold' color={colors.nightDark}>
                        Categorias
                    </Text>
                    <CounterRow>
                        <Text name='small' color={colors.night}>
                            Selecione até {MAX_TAGS} categorias para o seu evento.
                        </Text>
                        <Counter reached={isMaxReached}>
                            {selectedItems.length}/{MAX_TAGS}
                        </Counter>
                    </CounterRow>
                </HeaderContent>
            </SectionHeader>

            {tagsLoading ? <Loader /> : (
                <ChipsContainer>
                    {tags?.map(el => {
                        const isSelected = selectedItems.includes(el.id);
                        const isDisabled = isMaxReached && !isSelected;

                        return (
                            <Chip
                                key={el.id}
                                selected={isSelected}
                                disabled={isDisabled}
                                shake={shakeId === el.id}
                                onClick={() => handleSelectItem(el.id)}
                            >
                                {isSelected && <Check size={14} />}
                                <ChipLabel>{el.name}</ChipLabel>
                            </Chip>
                        );
                    })}
                </ChipsContainer>
            )}

            {error === 'min' && (
                <ErrorText>Selecione pelo menos uma categoria</ErrorText>
            )}

            {/* ── Seletor de ícone do mapa ── */}
            {selectedTagObjects.length > 0 && (
                <IconPickerSection>
                    <IconPickerHeader>
                        <MapPin size={15} color={colors.occasionalPurple} />
                        <Text name='small' type='bold' color={colors.nightDark}>
                            Ícone no mapa
                        </Text>
                        {!showIconPicker && (
                            <IconPickerHint>Selecionado automaticamente</IconPickerHint>
                        )}
                    </IconPickerHeader>

                    <IconOptionsRow>
                        {selectedTagObjects.map(tag => {
                            const IconComp = getCategoryIcon(tag.icon);
                            const isIconSelected = selectedIcon === tag.icon;
                            return (
                                <IconOption
                                    key={tag.id}
                                    selected={isIconSelected}
                                    disabled={!showIconPicker}
                                    onClick={() => showIconPicker && handleIconSelect(tag.icon)}
                                    title={tag.name}
                                >
                                    <IconOptionInner selected={isIconSelected}>
                                        <IconComp
                                            size={20}
                                            color={isIconSelected ? colors.neutralLight : colors.occasionalPurple}
                                        />
                                    </IconOptionInner>
                                    <IconOptionLabel selected={isIconSelected}>
                                        {tag.name}
                                    </IconOptionLabel>
                                    {isIconSelected && (
                                        <SelectedDot />
                                    )}
                                </IconOption>
                            );
                        })}
                    </IconOptionsRow>
                </IconPickerSection>
            )}
        </Column>
    )
}

/* ── Styled Components ── */

const SectionHeader = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin-bottom: 16px;
`;

const SectionIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: #F3EAFF;
    flex-shrink: 0;
    margin-top: 2px;
`;

const HeaderContent = styled.div`
    flex: 1;
`;

const CounterRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
`;

const Counter = styled.span`
    font-size: 12px;
    font-weight: 600;
    padding: 2px 10px;
    border-radius: 12px;
    white-space: nowrap;
    background: ${({ reached }) => reached ? '#F3EAFF' : colors.neutralDark};
    color: ${({ reached }) => reached ? colors.occasionalPurple : colors.night};
    transition: all .2s ease;
`;

const ChipsContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

const Chip = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 8px 16px;
    border-radius: 20px;
    font-size: 13px;
    cursor: pointer;
    user-select: none;
    transition: all .2s ease;
    border: 1.5px solid ${({ selected }) => selected ? colors.occasionalPurple : colors.nightLight};
    background: ${({ selected }) => selected ? colors.occasionalPurple : 'transparent'};
    color: ${({ selected }) => selected ? colors.neutralLight : colors.nightDark};
    opacity: ${({ disabled }) => disabled ? 0.45 : 1};
    pointer-events: ${({ disabled }) => disabled ? 'none' : 'auto'};

    ${({ shake }) => shake && `
        animation: shake 0.4s ease;
        pointer-events: auto;
        opacity: 0.6;
    `}

    &:hover {
        border-color: ${({ selected }) => selected ? colors.occasionalPurple : colors.night};
        ${({ selected }) => !selected && `background: ${colors.neutralBackground};`}
    }

    @keyframes shake {
        0%, 100% { transform: translateX(0); }
        20%, 60% { transform: translateX(-3px); }
        40%, 80% { transform: translateX(3px); }
    }

    @media screen and (min-width: ${breakpoints.lg}) {
        padding: 8px 20px;
    }
`;

const ChipLabel = styled.span`
    white-space: nowrap;
    font-weight: 600;
`;

const ErrorText = styled.p`
    color: ${colors.danger};
    font-size: 12px;
    margin-top: 10px;
    padding-left: 2px;
`;

/* ── Icon Picker ── */

const IconPickerSection = styled.div`
    margin-top: 20px;
    padding: 16px;
    background: #faf7ff;
    border: 1.5px solid #e9d8ff;
    border-radius: 12px;
`;

const IconPickerHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 14px;
`;

const IconPickerHint = styled.span`
    font-size: 11px;
    color: ${colors.night};
    background: ${colors.neutralDark};
    padding: 2px 8px;
    border-radius: 10px;
    margin-left: 4px;
`;

const IconOptionsRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
`;

const IconOption = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    cursor: ${({ disabled }) => disabled ? 'default' : 'pointer'};
    position: relative;
    transition: transform .15s ease;

    ${({ disabled }) => !disabled && `
        &:hover {
            transform: scale(1.06);
        }
    `}
`;

const IconOptionInner = styled.div`
    width: 52px;
    height: 52px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2.5px solid ${({ selected }) => selected ? colors.occasionalPurple : '#d8c8f5'};
    background: ${({ selected }) => selected ? colors.occasionalPurple : colors.neutralLight};
    transition: all .2s ease;
    box-shadow: ${({ selected }) => selected ? `0 0 0 3px #e9d8ff` : 'none'};
`;

const IconOptionLabel = styled.span`
    font-size: 10px;
    font-weight: 600;
    color: ${({ selected }) => selected ? colors.occasionalPurple : colors.night};
    text-align: center;
    max-width: 60px;
    line-height: 1.3;
`;

const SelectedDot = styled.div`
    position: absolute;
    top: -3px;
    right: -3px;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: ${colors.occasionalPurple};
    border: 2px solid white;

    &::after {
        content: '';
        position: absolute;
        top: 2px;
        left: 2px;
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: white;
    }
`;
